// @ts-strict-ignore
import { useState, useEffect, useContext, forwardRef } from "react";
import styles from "./lunr-search.module.less";
import { debounce } from "lodash-es";
import { Link } from "gatsby";
import { EventAggregator, LanguageHelper, Store } from "@parksandresorts/core";
import { SearchHelper } from "@parksandresorts/core";
import { useLunr } from "@parksandresorts/core";
import GlobalContext from "../../stores/global-context";

const searchPageviewDebounced = debounce((query: string) => {
  EventAggregator.publish("trackPageview", {
    virtualPagePath: "/search?q=" + query,
    virtualPageTitle: "Searched for " + query,
  });
}, 1000);

export type StoreItem = {
  id: string;
  title: string;
  slug: string;
};

type Props = {
  noSearchResultText?: string;
  searchPlaceHolder?: string;
  searchResultHeading?: string;
};

function LunrSearch(props: Props, ref: any) {
  const context = useContext(GlobalContext);

  const noSearchResultText = props.noSearchResultText ? props.noSearchResultText : "";
  const searchPlaceHolder = props.searchPlaceHolder ? props.searchPlaceHolder : "";
  const searchResultHeading = props.searchResultHeading ? props.searchResultHeading : "";

  const [queryString, setQueryString] = useState("");
  const [lunrQuery, setLunrQuery] = useState<string | undefined>(undefined);
  const [index, setIndex] = useState<string | undefined>(undefined);
  const [store, setStore] = useState<Store<StoreItem> | undefined>(undefined);

  const results = useLunr<StoreItem>(lunrQuery, index, store);
  const filteredResults = results.filter((page) => page.slug).slice(0, 10);

  useEffect(() => {
    SearchHelper.getSingelton<StoreItem>()
      .getLunrData(LanguageHelper.getCurrentLocale())
      .then(({ index, store }) => {
        setIndex(index);
        setStore(store);
      });
  }, []);

  const onClickLink = () => {
    context.setValue({ showSearch: false, isMainVisible: true });
    searchPageviewDebounced.flush();
  };

  const handleQueryStringChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQueryString = e.target.value;
    setQueryString(newQueryString);

    if (newQueryString.length < 2) {
      context.setValue({ isMainVisible: true });
      setLunrQuery("");
      return;
    }
    const queryStrings = e.target.value
      .toLowerCase()
      .split(" ")
      .filter((item) => item.length > 1);

    if (queryStrings.length === 0) {
      return;
    }

    // https://lunrjs.com/guides/searching.html
    const query = queryStrings
      .map((item) => `title:${item}^100 title:*${item}*^30 keywords:${item}^30 keywords:*${item}*^5`)
      .join(" ");

    setLunrQuery(query);
    searchPageviewDebounced(e.target.value);
  };

  return (
    <div className={styles.search}>
      <div className={styles.search__inputfieldWrapper}>
        <span className={["icon-search", styles.search__magnifyingGlass].join(" ")}></span>
        <input
          ref={ref}
          className={styles.search__inputfield}
          type="text"
          value={queryString}
          onChange={handleQueryStringChange}
          placeholder={searchPlaceHolder}
        />
      </div>

      <ul
        className={[
          styles.search__list,
          queryString.length > 1 ? styles.search__list_active : "",
          "block--max-width-narrow",
        ].join(" ")}
      >
        {queryString.length > 1 && <li className={styles.search__listItemHeading}>{searchResultHeading}</li>}
        {filteredResults.map((page) => (
          <li className={styles.search__listItem} key={page.title + page.id}>
            <Link to={page.slug} className={styles.search__listItemUrl} onClick={() => onClickLink()}>
              {page.title}
            </Link>
          </li>
        ))}
        {queryString.length > 1 && results.length === 0 && (
          <li className={[styles.search__listItem, styles.search__listItem_noResult].join(" ")} key="no_search_result">
            {noSearchResultText}
          </li>
        )}
      </ul>
    </div>
  );
}

export default forwardRef(LunrSearch);
